import React, { useMemo } from "react";
import { IonChip, IonText } from "@ionic/react";
import { Event, EventEntry } from "../../../models";
import { formatStartEndDatesStrings } from "../../../utilities/dates/FormatDates";
import { FormattedEntryData, FormattedRow, groupCompletedEntriesByBarn, isBarnAccountEntry } from "../../../utilities/stablingRequest/StablingAndExtrasUtil";
import { FormattedWaiverData, getChipTitle } from "../../../utilities/eventRules/WaiversUtils";
import { formatUSDCurrency } from "../../../utilities/currency/Currency";
import SubSectionCard from "./SubSectionCard";
import EntrySubSectionCard from "./EntrySubSectionCard";
import EntryStatusChip from "./EntryStatusChip";
import SummaryItemRow from "./EntrySummary/SummaryItemRow";
import SummaryItemRowWithUnderlay from "./EntrySummary/SummaryItemRowWithUnderlay";
import SummarySubtotalRow from "./EntrySummary/SummarySubtotalRow";
import EventSummaryCard from "./EntrySummary/EventSummaryCard";
import { FormattedEventClassData } from "../../../utilities/eventClass/EventClassUtils";
import EntryCardChip from "../../Chips/EntryCardChip";

/** Item Quantity totals by stabling category from saved stabling/bedding rows. */
export interface StablingExtrasTotals {
    stalls: number;
    feedItems: number;
    beddingItems: number;
    extraFees: number;
}

/** Dollar totals by stabling category from saved stabling/bedding rows (same buckets as `aggregateStablingTotals`). */
interface StablingAndExtrasUsdByCategory {
    stallsUsd: number;
    feedUsd: number;
    beddingUsd: number;
    extrasUsd: number;
}

/** Item Quantity totals by required fee category */
export interface RequiredFeeTotals {
    requiredFees: number;
}

/** Dollar totals by required fee category */
export interface RequiredFeeUSD {
    requiredFees: number;
}


/** Targets for scrolling from Entry Summary to the related New Entry section. */
export type EntrySummaryNavigateTarget =
    | { section: "header" }
    | { section: "stabling"; eventEntryId?: string }
    | { section: "waivers"; eventEntryId?: string }
    | { section: "entry"; entryIndex: number; focus?: "classes" }
    | { section: "showFees" };

interface EntrySummarySectionProps {
    event: Event;
    completedEventEntries?: EventEntry[];
    formattedClassFeesData?: FormattedEventClassData[] | null;
    formattedStablingData?: FormattedEntryData[] | null;
    waiversData?: FormattedWaiverData[] | null;
    /** When true, matches main Waivers card chip (`Loading`). */
    waiversLoading?: boolean;
    /** When true, class-fee amounts show `Loading` instead of a stale or $0 total. */
    classFeesLoading?: boolean;
    onNavigateToSection?: (target: EntrySummaryNavigateTarget) => void;
    summaryView?: "totals" | "byEntry";
    onSummaryViewChange?: (view: "totals" | "byEntry") => void;
}

const EntrySummarySection: React.FC<EntrySummarySectionProps> = ({
    event,
    completedEventEntries,
    formattedClassFeesData,
    formattedStablingData,
    waiversData,
    waiversLoading = false,
    classFeesLoading = false,
    onNavigateToSection,
    summaryView = "byEntry",
    onSummaryViewChange,
}) => {
    const go = onNavigateToSection;

    const startDate = event.startDate;
    const endDate = event.endDate;
    const dateRange = startDate && endDate ? formatStartEndDatesStrings(startDate, endDate, "MMM D, YYYY") : (startDate ?? "—");

    const allEntries = completedEventEntries ?? [];
    /** All entries (any status) used for the By Entry view rendering. */
    const allEntryRows = useMemo(() => {
        const all = completedEventEntries ?? [];
        return all.map((entry, entryIndexInAll) => ({ entry, entryIndexInAll }));
    }, [completedEventEntries]);
    const totalEntriesCount = allEntries.length;
    const entriesTitle = `${totalEntriesCount} ${totalEntriesCount === 1 ? "Entry" : "Entries"} / Class Entry Fees`;
    const entriesSectionStatusTitleValue = entriesSectionStatusTitle(allEntries);

    /** Same status labels as the main Waivers `EntrySectionCard` chip (`EntryCardChip` colors). */
    const waiverDisplayTitle = waiversLoading ? "Loading" : getChipTitle(waiversData ?? []);
    const waiverStatusChipClass = waiverStatusChipClassFromTitle(waiverDisplayTitle);

    /** Total waiver rule rows across all completed entries (each entry × its rules). */
    const totalWaiverRuleCount = useMemo(() => (waiversData ?? []).reduce((sum, w) => sum + w.rules.length, 0), [waiversData]);
    const waiversSectionTitle = totalWaiverRuleCount > 0 ? countPhrase(totalWaiverRuleCount, "Waiver", "Waivers") : "Waivers";

    /** Item Quantity totals for stabling and extras by category. */
    const stablingTotals: StablingExtrasTotals = aggregateStablingTotals(formattedStablingData ?? null);
    const stablingItemTotal: number = stablingTotals.stalls + stablingTotals.feedItems + stablingTotals.beddingItems + stablingTotals.extraFees;
    const stablingSectionTitle: string = stablingItemTotal > 0 ? `${stablingItemTotal} Stabling & Extras` : "Stabling & Extras";

    /** USD totals for stabling and extras by category. */
    const stablingAndExtrasUsdByCategory: StablingAndExtrasUsdByCategory = useMemo(() => aggregateStablingUSDTotals(formattedStablingData ?? null), [formattedStablingData]);
    const stablingSubtotalUsd = stablingAndExtrasUsdByCategory.stallsUsd + stablingAndExtrasUsdByCategory.feedUsd + stablingAndExtrasUsdByCategory.beddingUsd + stablingAndExtrasUsdByCategory.extrasUsd;

    /** USD total of class fees for all completed entries. */
    const entriesSubtotalUsd: number = aggregateClassFeesAmountsForAllCompletedEntries(completedEventEntries ?? []);
    
    /** Entries + Stabling; used for grand total (no standalone row). */
    const showFeesSubtotalUsd = aggregateRequiredFeeUsdForAllEntries(formattedStablingData ?? null);
    const grandTotalUsd = entriesSubtotalUsd + stablingSubtotalUsd + showFeesSubtotalUsd;
    
    function getClassFeesAmountForEntry(eventEntry: EventEntry): number {
        let total: number = 0;
        const classFeesData = formattedClassFeesData?.find((d) => d.eventEntry?.id === eventEntry.id);
        if (classFeesData) {
            total = classFeesData.totalAmount;
        }
        return total;
    }

    function classFeeValueDisplay(amountUsd: number): React.ReactNode {
        if (classFeesLoading) {
            return <EntryCardChip chipTitle="Loading" />;
        }
        return formatUSDCurrency(amountUsd);
    }

    const barnAccountSections = useMemo(() => {
        const seenBarnIds = new Set<string>();
        return (formattedStablingData ?? []).filter((d) => {
            if (!isBarnAccountEntry(d)) return false;
            if (d.id === "barn-account") return false;
            const key = d.barnId || d.id;
            if (seenBarnIds.has(key)) return false;
            seenBarnIds.add(key);
            return true;
        });
    }, [formattedStablingData]);

    const barnDisplayGroups = useMemo(() => groupCompletedEntriesByBarn(allEntries), [allEntries]);

    const barnIdsFromAllEntries = useMemo(() => {
        const byBarnId = new Map<string, string>();
        for (const { entry } of allEntryRows) {
            if (!entry.barnId) continue;
            if (!byBarnId.has(entry.barnId)) {
                byBarnId.set(entry.barnId, entry.barnName?.trim() || entry.barn?.name?.trim() || "Barn Account");
            }
        }
        return Array.from(byBarnId.entries())
            .map(([barnId, barnName]) => ({ barnId, barnName }))
            .sort((a, b) => a.barnName.localeCompare(b.barnName, undefined, { sensitivity: "base" }));
    }, [allEntryRows]);

    const entriesWithoutBarn = useMemo(
        () => allEntryRows.filter(({ entry }) => !entry.barnId),
        [allEntryRows]
    );
    function aggregateClassFeesAmountsForAllCompletedEntries(completedEventEntries: EventEntry[]): number {
        let runningTotalForAllCompletedEntries = 0;
        for (const completedEntry of completedEventEntries) {
            const total = getClassFeesAmountForEntry(completedEntry);
            runningTotalForAllCompletedEntries += total;
        }
        return runningTotalForAllCompletedEntries;
    }

    /** Shown in entry subcard header when collapsed (status chip only; totals live in collapsedContent). */
    function summaryEntryStatusChip(isComplete: boolean): React.ReactNode {
        return <EntryStatusChip isComplete={isComplete} completeLabel="Completed" incompleteLabel="Incomplete" />;
    }

    function entriesSectionStatusTitle(entries: EventEntry[]): "Not Started" | "Incomplete" | "Completed" {
        if (!entries.length) return "Not Started";
        if (entries.every((entry) => entry.status === "complete")) return "Completed";
        return "Incomplete";
    }

    function summaryCollapsedHeaderTitleAndSubtotal(statusTitle: string, amountUsd: number): React.ReactNode {
        const statusClass = waiverStatusChipClassFromTitle(statusTitle);
        return <IonChip className={`m-0 rounded-2 text-white font-weight-bold ${statusClass}`}>{statusTitle}</IonChip>;
    }

    /** Same chip colors as the main Waivers card (`EntryCardChip`). */
    function waiverStatusChipClassFromTitle(waiverDisplayTitle: string): string {
        if (waiverDisplayTitle === "Completed") return "bg-success";
        if (waiverDisplayTitle === "Incomplete") return "bg-warning";
        if (waiverDisplayTitle === "Loading") return "bg-dark";
        return "bg-danger";
    }

    function entryStatusChipClassFromStatus(entryStatus: EventEntry["status"]): string {
        if (entryStatus === "complete") return "bg-success";
        return "bg-warning";
    }

    function categorizeBeddingRow(row: FormattedRow): "feed" | "bedding" | "extra" {
        const type = (row.item.eventBeddingType?.type || "").toLowerCase();
        const label = row.item.label.toLowerCase();
        if (type.includes("feed") || label.includes("feed")) return "feed";
        if (type.includes("extra") || label.includes("extra")) return "extra";
        return "bedding";
    }
    
    /** e.g. `1 Stall`, `3 Stalls`, `0 Feed Items` (0 uses plural). */
    function countPhrase(count: number, singular: string, plural: string): string {
        return `${count} ${count === 1 ? singular : plural}`;
    }
    
    function formatEntryHorseAndRiders(entry: EventEntry): string {
        const horse = entry.horseName?.trim() || "Horse";
        const rider = entry.riderName?.trim() || "Rider";
        const second = entry.secondRiderName?.trim() || entry.secondRider?.name?.trim();
        const base = `${horse} & ${rider}`;
        if (second) return `${base} & ${second}`;
        return base;
    }

    /** For a single entry, roll up stabling quantity totals. */
    function aggregateStablingTotalsForEntry(formattedEntryData: FormattedEntryData | null | undefined): StablingExtrasTotals {
        let totals: StablingExtrasTotals = { stalls: 0, feedItems: 0, beddingItems: 0, extraFees: 0 };
        if (!formattedEntryData) return totals;
    
        for (const row of formattedEntryData.rows) {
            if (row.item.eventStallType || row.item.previousStablingRequest) {
                totals.stalls += row.quantity;
                continue;
            }
            if (row.item.eventBeddingType || row.item.previousBeddingRequest || row.item.eventBeddingTypeId) {
                const cat = categorizeBeddingRow(row);
                if (cat === "feed") totals.feedItems += row.quantity;
                else if (cat === "extra") totals.extraFees += row.quantity;
                else totals.beddingItems += row.quantity;
            }
            if (row.item.eventFee || row.item.previousEventEntryFee) {
                if (!row.item.isRequired) {
                    totals.extraFees += row.quantity
                }
            }
        }
        return totals;
    }
    
    /** For all of the given entries, roll up stabling quantity totals. */
    function aggregateStablingTotals(formattedData: FormattedEntryData[] | null | undefined): StablingExtrasTotals {
        const totals: StablingExtrasTotals = { stalls: 0, feedItems: 0, beddingItems: 0, extraFees: 0};
        if (!formattedData?.length) return totals;
    
        for (const entry of formattedData) {
            const entryTotals = aggregateStablingTotalsForEntry(entry);
            totals.stalls += entryTotals.stalls;
            totals.feedItems += entryTotals.feedItems;
            totals.beddingItems += entryTotals.beddingItems;
            totals.extraFees += entryTotals.extraFees;
        }
        return totals;
    }

    /** For a single entry, roll up stabling USD totals. */
    function aggregateStablingUSDTotalsForEntry(formattedEntryData: FormattedEntryData | null | undefined): StablingAndExtrasUsdByCategory {
        let totals: StablingAndExtrasUsdByCategory = { stallsUsd: 0, feedUsd: 0, beddingUsd: 0, extrasUsd: 0 };
        if (!formattedEntryData) return totals;
    
        for (const row of formattedEntryData.rows) {
            const lineUsd = row.quantity * dollarsFromCostString(row.item.cost);
            if (row.item.eventStallType || row.item.previousStablingRequest) {
                totals.stallsUsd += lineUsd;
                continue;
            }
            if (row.item.eventBeddingType || row.item.previousBeddingRequest || row.item.eventBeddingTypeId) {
                const cat = categorizeBeddingRow(row);
                if (cat === "feed") totals.feedUsd += lineUsd;
                else if (cat === "extra") totals.extrasUsd += lineUsd;
                else totals.beddingUsd += lineUsd;
            }
            if (row.item.eventFee || row.item.previousEventEntryFee) {
                if (!row.item.isRequired) {
                    totals.extrasUsd += lineUsd;
                }
            }
        }
        return totals;
    }
    
    /** For all of the given entries, roll up stabling USD totals. */
    function aggregateStablingUSDTotals(formattedData: FormattedEntryData[] | null | undefined): StablingAndExtrasUsdByCategory {
        const totals: StablingAndExtrasUsdByCategory = { stallsUsd: 0, feedUsd: 0, beddingUsd: 0, extrasUsd: 0};
        if (!formattedData?.length) return totals;
    
        for (const entry of formattedData) {
            const entryTotals: StablingAndExtrasUsdByCategory = aggregateStablingUSDTotalsForEntry(entry);
            totals.stallsUsd += entryTotals.stallsUsd;
            totals.feedUsd += entryTotals.feedUsd;
            totals.beddingUsd += entryTotals.beddingUsd;
            totals.extrasUsd += entryTotals.extrasUsd;
        }
        return totals;
    }
    
    // For a single entry, roll up required fee quantities.
    function aggregateRequiredFeeTotalsForEntry(formattedEntryData: FormattedEntryData | null | undefined): RequiredFeeTotals {
        let totals: RequiredFeeTotals = { requiredFees: 0 };
        if (!formattedEntryData) return totals;
    
        for (const row of formattedEntryData.rows) {
            if (row.item.eventFee || row.item.previousEventEntryFee) {
                if (row.item.isRequired) {
                    totals.requiredFees += row.quantity;
                }
            }
        }
        return totals;
    }

    /** For all of the given entries, roll up required fee quantities. */
    function aggregateRequiredFeeTotals(formattedData: FormattedEntryData[] | null | undefined): RequiredFeeTotals {
        const totals: RequiredFeeTotals = { requiredFees: 0 };
        if (!formattedData?.length) return totals;
    
        for (const entry of formattedData) {
            const entryTotals = aggregateRequiredFeeTotalsForEntry(entry);
            totals.requiredFees += entryTotals.requiredFees;
        }
        return totals;
    }

    /** For a single entry, roll up required fee USD totals. */
    function aggregateRequiredFeeUsdForEntry(formattedEntryData: FormattedEntryData | null | undefined): number {
        let requiredFeesUsd: number = 0;
        if (!formattedEntryData) return requiredFeesUsd;
        for (const row of formattedEntryData.rows) {
            const lineUsd = row.quantity * dollarsFromCostString(row.item.cost);
            if (row.item.eventFee || row.item.previousEventEntryFee) {
                if (row.item.isRequired) {
                    requiredFeesUsd += lineUsd
                }
            }
        }
        return requiredFeesUsd;
    }

    /** For all of the given entries, roll up required fee USD totals. */
    function aggregateRequiredFeeUsdForAllEntries(formattedData: FormattedEntryData[] | null | undefined): number {
        let requiredFeesUsd: number = 0;
        if (!formattedData?.length) return requiredFeesUsd;
        for (const entry of formattedData) {
            const entryUsd = aggregateRequiredFeeUsdForEntry(entry);
            requiredFeesUsd += entryUsd;
        }
        return requiredFeesUsd;
    }
    
    function dollarsFromCostString(cost: string | null | undefined): number {
        if (!cost) return 0;
        const n = parseFloat(String(cost).replace(/[^0-9.-]/g, ""));
        return Number.isFinite(n) ? n : 0;
    }
    
    function rowLineUsd(row: FormattedRow): number {
        return row.quantity * dollarsFromCostString(row.item.cost);
    }
    
    function sumRowsUsd(rows: FormattedRow[]): number {
        return rows.reduce((sum, row) => sum + rowLineUsd(row), 0);
    }
    
    const goToStablingForEntry = (entry: EventEntry) =>
        go?.({ section: "stabling", ...(entry.id ? { eventEntryId: entry.id } : {}) });

    const stablingRowsForEntry = (entry: EventEntry) => {
        const formattedEntryData = formattedStablingData?.find((item) => (item.eventEntry?.id === entry.id && item.isBarnAccount !== true)) ?? null;
        if (!formattedEntryData) return null;
        const totals = aggregateStablingTotalsForEntry(formattedEntryData);
        const usd = aggregateStablingUSDTotalsForEntry(formattedEntryData);
        const rowDefs = [
            { count: totals.stalls, singular: "Stall", plural: "Stalls", amountUsd: usd.stallsUsd },
            { count: totals.feedItems, singular: "Feed Item", plural: "Feed Items", amountUsd: usd.feedUsd },
            { count: totals.beddingItems, singular: "Bedding Item", plural: "Bedding Items", amountUsd: usd.beddingUsd },
            { count: totals.extraFees, singular: "Extra", plural: "Extras", amountUsd: usd.extrasUsd }
        ].filter((row) => row.count > 0);

        return (
            <>
            {rowDefs.length > 0 && 
                    <>
                        {rowDefs.map((row) => (
                            <SummaryItemRow
                                key={row.plural}
                                label={countPhrase(row.count, row.singular, row.plural)}
                                value={formatUSDCurrency(row.amountUsd)}
                                onEditClick={go ? () => goToStablingForEntry(entry) : undefined}
                            />
                        ))}
                    </>
                }
            </>
        );
    };
    
    const stablingRowsForTotals = useMemo(() => {
        const totals = aggregateStablingTotals(formattedStablingData ?? null);
        const usd = stablingAndExtrasUsdByCategory;
        const rowDefs = [
            { count: totals.stalls, singular: "Stall", plural: "Stalls", amountUsd: usd.stallsUsd },
            { count: totals.feedItems, singular: "Feed Item", plural: "Feed Items", amountUsd: usd.feedUsd },
            { count: totals.beddingItems, singular: "Bedding Item", plural: "Bedding Items", amountUsd: usd.beddingUsd },
            { count: totals.extraFees, singular: "Extra", plural: "Extras", amountUsd: usd.extrasUsd }
        ].filter((row) => row.count > 0);

        return (
            <>
            {rowDefs.length > 0 &&
                    <>
                        {rowDefs.map((row) => (
                            <SummaryItemRow
                                key={row.plural}
                                label={countPhrase(row.count, row.singular, row.plural)}
                                value={formatUSDCurrency(row.amountUsd)}
                                onEditClick={go ? () => go({ section: "stabling" }) : undefined}
                            />
                        ))}
                    </>
                }
            </>
        );
    }, [go, formattedStablingData, stablingAndExtrasUsdByCategory]);

    const showFeesRowsForTotals = useMemo(() => {
        const totals = aggregateRequiredFeeTotals(formattedStablingData ?? null);
        const usd = aggregateRequiredFeeUsdForAllEntries(formattedStablingData ?? null);
        const rowDefs = [
            { count: totals.requiredFees, singular: "Required Fee", plural: "Required Fees", amountUsd: usd },
        ].filter((row) => row.count > 0);

        return (
            <>
                {rowDefs.length > 0 ? 
                    <>
                        {rowDefs.map((row) => (
                            <SummaryItemRow
                                key={row.plural}
                                label={countPhrase(row.count, row.singular, row.plural)}
                                value={formatUSDCurrency(row.amountUsd)}
                                onEditClick={go ? () => go({ section: "stabling" }) : undefined}
                            />
                        ))}
                    </>
                :
                    <SummaryItemRow
                        key={0}
                        label={"No show fees found."}
                        value={"$0.00"}
                        onEditClick={go ? () => go({ section: "stabling" }) : undefined}
                    />
                }
            </>
        );
    }, [go, formattedStablingData, stablingAndExtrasUsdByCategory]);

    const renderStablingItemRows = (rows: FormattedRow[], keyPrefix: string) =>
        rows.map((row, rowIndex) => (
            <SummaryItemRow
                key={`${keyPrefix}-row-${rowIndex}`}
                label={`${row.quantity} ${row.item.label}`}
                value={formatUSDCurrency(rowLineUsd(row))}
                onEditClick={go ? () => go({ section: "stabling" }) : undefined}
            />
        ));

    const renderBarnAccountSummaryCard = (barnData: FormattedEntryData, barnName: string) => {
        const barnTotalUsd = sumRowsUsd(barnData.rows);
        return (
            <div className="mb-2" key={barnData.id}>
                <SubSectionCard
                    title={barnName}
                    titleTertiaryLine="Barn Split Account — supplies to be split in the show office later"
                    leadingEmoji="🏠"
                    padding="px-0 py-1"
                    defaultExpanded={false}
                    allowWrappedTitle={true}
                    showChevron="expandedOnly"
                    collapsedContent={
                        <SummarySubtotalRow amountUsd={barnTotalUsd} label="Total" fontSize={18} bold={true} showTopBorder={false} removeTopSpacing={true} />
                    }>
                    <div className="px-2 py-1">
                        {barnData.rows.length > 0 ? (
                            renderStablingItemRows(barnData.rows, barnData.id)
                        ) : (
                            <div className="d-flex ion-justify-content-center px-1 py-1">
                                <IonText color="danger" className="entry-summary-empty-message">
                                    No stabling or extras added.
                                </IonText>
                            </div>
                        )}
                        <SummarySubtotalRow amountUsd={barnTotalUsd} label="Total" fontSize={18} bold={true} />
                    </div>
                </SubSectionCard>
            </div>
        );
    };

    const renderEntrySummaryCard = ({ entry, entryIndexInAll }: { entry: EventEntry; entryIndexInAll: number }) => {
        // Waiver data for this entry.
        const waiverForEntry = entry.id && waiversData ? waiversData.find((w) => w.id === entry.id || w.eventEntry?.id === entry.id) : undefined;
        const entryWaiverTitle = waiversLoading ? "Loading" : getChipTitle(waiverForEntry ? [waiverForEntry] : []);
        const entryWaiverChipClass = waiverStatusChipClassFromTitle(entryWaiverTitle);

        // Class fee data for this entry.
        const entryFeeTotalUsd: number = getClassFeesAmountForEntry(entry);

        // Stabling data for this entry.
        const stablingAndExtrasUsdByCategory: StablingAndExtrasUsdByCategory = aggregateStablingUSDTotalsForEntry(formattedStablingData?.find((item) => (item.eventEntry?.id === entry.id && item.isBarnAccount !== true)) ?? null);
        const stablingTotalUsd: number = stablingAndExtrasUsdByCategory.stallsUsd + stablingAndExtrasUsdByCategory.feedUsd + stablingAndExtrasUsdByCategory.beddingUsd + stablingAndExtrasUsdByCategory.extrasUsd;

        // Show fees data for this entry.
        const showFeesTotalUsd: number = aggregateRequiredFeeUsdForEntry(formattedStablingData?.find((item) => (item.eventEntry?.id === entry.id && item.isBarnAccount !== true)) ?? null);

        // Total USD for this entry.
        const entryTotalUsd: number = entryFeeTotalUsd + stablingTotalUsd + showFeesTotalUsd;

        // Entry Data status (same Incomplete / Completed chip as the Entry Card).
        const entryDataStatusTitle = entry.status === "complete" ? "Completed" : "Incomplete";
        const entryDataStatusChipClass = entryStatusChipClassFromStatus(entry.status);

        /** Rolled-up status: entry's own status AND every inner item must be Completed. */
        const isEntryRolledUpComplete: boolean = entry.status === "complete" && entryWaiverTitle === "Completed";

        return (
            <EntrySubSectionCard
                key={entry.id || entryIndexInAll}
                entry={entry}
                entryNumber={entryIndexInAll + 1}
                defaultExpanded={false}
                collapsedContent={
                    <SummarySubtotalRow amountUsd={entryTotalUsd} label="Total" fontSize={18} bold={true} showTopBorder={false} removeTopSpacing={true} isLoading={classFeesLoading} />
                }
                collapsedHeaderTrailing={summaryEntryStatusChip(isEntryRolledUpComplete)}>
                <div className="px-2 py-1">
                        <SummaryItemRow
                            label="Entry Data"
                            value={<IonChip className={`m-0 rounded-2 text-white font-weight-bold ${entryDataStatusChipClass}`}>{entryDataStatusTitle}</IonChip>}
                            onRowClick={go ? () => go({ section: "entry", entryIndex: entryIndexInAll }) : undefined}
                        />
                        <SummaryItemRow
                            label="Waivers"
                            value={<IonChip className={`m-0 rounded-2 text-white font-weight-bold ${entryWaiverChipClass}`}>{entryWaiverTitle}</IonChip>}
                            onRowClick={
                                go
                                    ? () =>
                                          go({
                                              section: "waivers",
                                              ...(entry.id ? { eventEntryId: entry.id } : {}),
                                          })
                                    : undefined
                            }
                        />
                        <SummaryItemRow
                            label="Class Entry Fees"
                            value={classFeeValueDisplay(entryFeeTotalUsd)}
                            isLoading={classFeesLoading}
                            onEditClick={go ? () => go({ section: "entry", entryIndex: entryIndexInAll, focus: "classes" }) : undefined}
                        />
                        {stablingRowsForEntry(entry)}
                        <SummaryItemRow
                            label="Required Show Fees"
                            value={formatUSDCurrency(showFeesTotalUsd)}
                            onEditClick={go ? () => goToStablingForEntry(entry) : undefined}
                        />
                    <SummarySubtotalRow amountUsd={entryTotalUsd} label="Total" fontSize={18} bold={true} isLoading={classFeesLoading} />
                </div>
            </EntrySubSectionCard>
        );
    };

    const noEntriesMessage = allEntries.length === 0 ? "No entries yet. Add an entry above." : undefined;

    const displayNoEntriesMessage = noEntriesMessage ? (
        <SummaryItemRow
            key={0}
            label={noEntriesMessage}
            value={"$0.00"}
            onEditClick={go ? () => go({ section: "entry", entryIndex: 0 }) : undefined}
        />
    ) : null;

    const byEntryView = (
        <>
            {allEntryRows.length === 0 ? (
                displayNoEntriesMessage
            ) : (
                <>
                    {barnIdsFromAllEntries.map((barnGroup, barnGroupIndex) => {
                        const barnData =
                            barnAccountSections.find((b) => b.barnId === barnGroup.barnId) ||
                            barnAccountSections.find((b) => (b.linkedEntries ?? []).some((e) => e.barnId === barnGroup.barnId));
                        const barnEntryRows = allEntryRows.filter(({ entry }) => entry.barnId === barnGroup.barnId);
                        const hasNextBarnGroup = barnGroupIndex < barnIdsFromAllEntries.length - 1;
                        const showBarnAccount = barnDisplayGroups.some((g) => g.barnId === barnGroup.barnId) && Boolean(barnData);

                        return (
                            <div key={barnGroup.barnId}>
                                {showBarnAccount && barnData ? renderBarnAccountSummaryCard(barnData, barnGroup.barnName) : null}
                                {barnEntryRows.map(({ entry, entryIndexInAll }) => renderEntrySummaryCard({ entry, entryIndexInAll }))}
                                {hasNextBarnGroup ? (
                                    <div role="separator" aria-hidden="true" className="entry-form-divider--spaced" />
                                ) : null}
                            </div>
                        );
                    })}
                    {entriesWithoutBarn.length > 0 && barnIdsFromAllEntries.length > 0 ? (
                        <div role="separator" aria-hidden="true" className="entry-form-divider" />
                    ) : null}
                    {entriesWithoutBarn.map(({ entry, entryIndexInAll }) => renderEntrySummaryCard({ entry, entryIndexInAll }))}
                    <div className="mb-2">
                        <div className="px-2 pt-1 mx-0">
                            <SummarySubtotalRow amountUsd={grandTotalUsd} label="Grand Total" fontSize={20} fontWeight={800} isLoading={classFeesLoading} />
                        </div>
                    </div>
                </>
            )}
        </>
    );

    const totalsView = (
        <>
            <div className="mb-2">
                <SubSectionCard
                    title={waiversSectionTitle}
                    variant="summaryRow"
                    leadingEmoji="📋"
                    onSummaryLeadingClick={go && !waiversLoading ? () => go({ section: "waivers" }) : undefined}>
                    {totalEntriesCount === 0 && !waiversLoading ? (
                        <IonText className="text-white entry-summary-chip-label">
                            Add an entry to review waivers.
                        </IonText>
                    ) : (
                        <IonChip className={`m-0 rounded-2 text-white font-weight-bold ${waiverStatusChipClass}`}>{waiverDisplayTitle}</IonChip>
                    )}
                </SubSectionCard>
            </div>

            <div className="mb-2">
                <SubSectionCard
                    title={entriesTitle}
                    padding="px-0 py-1"
                    leadingEmoji="🐴"
                    defaultExpanded={false}
                    showChevron="expandedOnly"
                    collapsedContent={
                        <SummarySubtotalRow amountUsd={entriesSubtotalUsd} label="Total" fontSize={18} bold={true} showTopBorder={false} removeTopSpacing={true} isLoading={classFeesLoading} />
                    }
                    collapsedHeaderTrailing={summaryCollapsedHeaderTitleAndSubtotal(entriesSectionStatusTitleValue, entriesSubtotalUsd)}>
                    <div className="px-2 py-1">
                        {allEntries.length === 0 ? (
                            displayNoEntriesMessage
                        ) : (
                            <>
                                {allEntries.map((e, i) => {
                                    const entryStatusTitle = e.status === "complete" ? "Completed" : "Incomplete";
                                    const entryStatusChipClass = entryStatusChipClassFromStatus(e.status);
                                    return (
                                        <SummaryItemRowWithUnderlay
                                            key={e.id || i}
                                            label={formatEntryHorseAndRiders(e)}
                                            trailing={<IonChip className={`m-0 rounded-2 text-white font-weight-bold ${entryStatusChipClass}`}>{entryStatusTitle}</IonChip>}
                                            underlayAmountUsd={getClassFeesAmountForEntry(e)}
                                            underlayLoading={classFeesLoading}
                                            onRowClick={go ? () => go({ section: "entry", entryIndex: i }) : undefined}
                                        />
                                    );
                                })}
                            </>
                        )}
                        <SummarySubtotalRow amountUsd={entriesSubtotalUsd} label="Total" fontSize={18} bold={true} isLoading={classFeesLoading} />
                    </div>
                </SubSectionCard>
            </div>

            <div className="mb-2">
                <SubSectionCard
                    title={stablingSectionTitle}
                    padding="px-0 py-1"
                    leadingEmoji="📦"
                    defaultExpanded={false}
                    collapsedContent={
                        <SummarySubtotalRow amountUsd={stablingSubtotalUsd} label="Total" fontSize={18} bold={true} showTopBorder={false} removeTopSpacing={true} />
                    }>
                    <div className="px-2 py-1">
                        {totalEntriesCount === 0 ? (
                            <IonText color="medium" className="display-4 px-1">
                                Add an entry to add stabling and extras.
                            </IonText>
                        ) : (
                            stablingRowsForTotals
                        )}
                        <SummarySubtotalRow amountUsd={stablingSubtotalUsd} label="Total" fontSize={18} bold={true} />
                    </div>
                </SubSectionCard>
            </div>

            <div className="mb-2">
                <SubSectionCard
                    title="Show Fees"
                    padding="px-0 py-1"
                    leadingEmoji="💵"
                    defaultExpanded={false}
                    collapsedContent={
                        <SummarySubtotalRow amountUsd={showFeesSubtotalUsd} label="Total" fontSize={18} bold={true} showTopBorder={false} removeTopSpacing={true} />
                    }>
                    <div className="px-2 py-1">
                        {totalEntriesCount === 0 ? (
                            <IonText color="medium" className="display-4 px-1">
                                Add an entry to see show fees here.
                            </IonText>
                        ) : (
                            showFeesRowsForTotals
                        )}
                        <SummarySubtotalRow amountUsd={showFeesSubtotalUsd} label="Total" fontSize={18} bold={true} />
                    </div>
                </SubSectionCard>
            </div>

            <div className="mb-2">
                <div className="px-2 pt-1 mx-0">
                    <SummarySubtotalRow amountUsd={grandTotalUsd} label="Grand Total" fontSize={20} fontWeight={800} isLoading={classFeesLoading} />
                </div>
            </div>
        </>
    );

    return (
        <>
            {/* Event info row. */}
            <EventSummaryCard eventName={event.name} dateRange={dateRange} onClick={() => go?.({ section: "header" })} />

            {/* Toggle between totals and by entry view buttons. */}
            <div className="mb-2 d-flex ion-justify-content-end">
                <div className="entry-summary-view-toggle">
                    <button
                        type="button"
                        onClick={() => onSummaryViewChange?.("byEntry")}
                        className={`entry-summary-view-toggle__btn${summaryView === "byEntry" ? " entry-summary-view-toggle__btn--active" : ""}`}
                        aria-pressed={summaryView === "byEntry"}>
                        By Entry
                    </button>
                    <button
                        type="button"
                        onClick={() => onSummaryViewChange?.("totals")}
                        className={`entry-summary-view-toggle__btn${summaryView === "totals" ? " entry-summary-view-toggle__btn--active" : ""}`}
                        aria-pressed={summaryView === "totals"}>
                        Totals
                    </button>
                </div>
            </div>

            {summaryView === "byEntry" ? (
                byEntryView
            ) : (
                totalsView
            )}
        </>
    );
};

export default EntrySummarySection;
